// Demo league configuration.
//
// demo.statharbour.com is a build-once, multi-sport showcase: one fully
// seeded public league per supported sport, reachable from a sport switcher
// at the root and from per-sport deep links (demo.statharbour.com/baseball,
// /flag-football, ...). A lightweight theming hook (?org=&primary=&secondary=
// &logo=) re-skins any of these demos with a prospect's name and colors over
// the same seed data, so one email template personalizes per lead at zero
// per-lead cost.
//
// The fixed league/season IDs below MUST match the IDs the seed generator
// emits (scripts/generate-demo-leagues.mjs -> supabase/seed_demo_leagues.sql).
// Basketball keeps its original IDs (a1/a2) so existing demo links and the
// subdomain redirect stay valid.

import type { CanonicalSport } from "@/config/sports/registry";

/** Organization that owns every demo league (one shared demo org). */
export const DEMO_ORG_ID = "0de10000-0000-4000-a000-0000000000a3";

export interface DemoLeague {
  /** Canonical sport id (drives stats, periods, labels downstream). */
  sport: CanonicalSport;
  /** Deep-link slug under the demo host, e.g. "flag-football". */
  slug: string;
  /** Fixed league id (matches the seed SQL). */
  leagueId: string;
  /** Fixed active-season id (matches the seed SQL). */
  seasonId: string;
  /** Default display name shown when no ?org override is present. */
  displayName: string;
  /** Short tagline for the switcher card. */
  tagline: string;
}

// One demo league per supported sport. Order is the switcher display order.
export const DEMO_LEAGUES: DemoLeague[] = [
  {
    sport: "soccer",
    slug: "soccer",
    leagueId: "0de10000-0000-4000-a000-0000000000b1",
    seasonId: "0de10000-0000-4000-a000-0000000000b2",
    displayName: "Coastal Premier Division",
    tagline: "Tier one of the Coastal pyramid. Standings, top scorers, clean sheets and match reports.",
  },
  {
    sport: "basketball",
    slug: "basketball",
    leagueId: "0de10000-0000-4000-a000-0000000000a1",
    seasonId: "0de10000-0000-4000-a000-0000000000a2",
    displayName: "Continental Basketball Conference",
    tagline: "Two conferences, four divisions. Box scores, shooting splits and a live postseason bracket.",
  },
  {
    sport: "baseball",
    slug: "baseball",
    leagueId: "0de10000-0000-4000-a000-0000000000c1",
    seasonId: "0de10000-0000-4000-a000-0000000000c2",
    displayName: "Continental Baseball Association",
    tagline: "R-H-E line scores, AVG/OBP/SLG, pitching leaders and a live postseason bracket.",
  },
  {
    sport: "softball",
    slug: "softball",
    leagueId: "0de10000-0000-4000-a000-0000000000d1",
    seasonId: "0de10000-0000-4000-a000-0000000000d2",
    displayName: "Continental Softball Association",
    tagline: "Seven-inning line scores, batting and pitching leaders across the association.",
  },
  {
    sport: "flag_football",
    slug: "flag-football",
    leagueId: "0de10000-0000-4000-a000-0000000000e1",
    seasonId: "0de10000-0000-4000-a000-0000000000e2",
    displayName: "National Flag Football Conference",
    tagline: "Passing, rushing and receiving lines, flag pulls and passer rating.",
  },
];

/** Path to a league's public hub page. */
export function demoPublicPath(league: DemoLeague): string {
  return `/public/${league.leagueId}/${league.seasonId}`;
}

/** Look up a demo league by its deep-link slug. */
export function getDemoLeagueBySlug(slug: string): DemoLeague | undefined {
  return DEMO_LEAGUES.find((l) => l.slug === slug);
}

/** Look up a demo league by canonical sport id. */
export function getDemoLeagueBySport(sport: string): DemoLeague | undefined {
  return DEMO_LEAGUES.find((l) => l.sport === sport);
}

/** True when a league id belongs to one of the demo leagues. */
export function isDemoLeagueId(leagueId: string | null | undefined): boolean {
  if (!leagueId) return false;
  return DEMO_LEAGUES.some((l) => l.leagueId === leagueId);
}

// Backward-compatible exports: the basketball demo's IDs and public path, kept
// so the demo subdomain redirect and existing /demo link continue to resolve.
const BASKETBALL_DEMO = getDemoLeagueBySport("basketball")!;
export const DEMO_LEAGUE_ID = BASKETBALL_DEMO.leagueId;
export const DEMO_SEASON_ID = BASKETBALL_DEMO.seasonId;
export const DEMO_LEAGUE_PUBLIC_PATH = demoPublicPath(BASKETBALL_DEMO);

/**
 * Returns true when the app is being served from the demo subdomain
 * (for example demo.statharbour.com or demo.localhost).
 */
export function isDemoHost(hostname: string): boolean {
  return hostname === "demo.statharbour.com" || hostname.startsWith("demo.");
}

/** Canonical home of the public demo experience. */
export const DEMO_SITE_URL = "https://demo.statharbour.com";

/**
 * URL that the public "View Demo" entry points should target.
 *
 * The demo switcher is reachable two ways from the same build: the
 * demo.statharbour.com subdomain (its canonical home) and the in-app /demo
 * route on any host. From the production sports marketing site we send
 * visitors to the subdomain; on localhost, Vercel previews, or when already
 * on the demo host we keep the relative /demo route so the link still
 * resolves without a live subdomain. During SSR/prerender there is no window,
 * and the marketing pages are prerendered only for the production sports site,
 * so we default to the subdomain there too.
 *
 * The result can be a cross-origin subdomain URL, so callers must render it
 * with a plain <a href>, not react-router's <Link>.
 */
export function demoSiteUrl(hostname?: string): string {
  const host =
    hostname ?? (typeof window !== "undefined" ? window.location.hostname : undefined);
  if (host === undefined || host === "statharbour.com" || host === "www.statharbour.com") {
    return DEMO_SITE_URL;
  }
  return "/demo";
}
